<template>
  <div
    v-if="
      !loading &&
      ['fail', 'incomplete', 'partial_pass'].includes(rulesAssessmentStatus)
    "
  >
    <img
      v-if="brokerLogo"
      class="mx-auto mt-8 hidden h-11 w-auto md:block"
      :src="brokerLogo"
      alt="broker logo"
    />
    <component
      :is="statusComponents[rulesAssessmentStatus]"
      :company-type="companyType"
      :loading="loading"
      :company-name="companyName"
      :support-email="supportEmail"
      :failed-rules="failedRulesByClassification"
      :unique-failed-rules="uniqueFailedRules"
      :incomplete-rules="incompleteRulesByClassification"
      :unique-incomplete-rules="uniqueIncompleteRules"
      :total-rules="totalRules"
      :broker-logo="brokerLogo"
      :rules-syntax="rulesSyntax"
    />
  </div>
  <div
    v-else
    class="tablet-desktop-container mx-auto flex h-80 max-w-2xl items-center justify-center overflow-scroll pt-12 md:overflow-hidden"
  >
    <img
      class="-top-2px loading relative h-12 w-full"
      src="@/assets/loading-v2-yellow.svg"
      alt=""
    />
  </div>
</template>
<script setup>
/* eslint-disable no-unused-vars */
import { computed, watchEffect, onMounted } from 'vue';
import { useStore } from 'vuex';
import IncompleteAssessment from '@/components/holding-tanks/IncompleteAssessment.vue';
import FailedAssessment from '@/components/holding-tanks/FailedAssessment.vue';

const statusComponents = {
  incomplete: IncompleteAssessment,
  fail: FailedAssessment,
  partial_pass: IncompleteAssessment,
};

const store = useStore();

const companyType = computed(
  () => store.state.users.currentUser?.company?.company_type
);
const companyName = computed(
  () => store.state.users.currentUser?.company?.legal_name
);
const brokerLogo = computed(() => store.state.company.company?.logo?.url);
const loading = computed(() => store.state.loading.isLoading);
const supportEmail = computed(() => store.state.company.company?.support_email);
const rulesSyntax = computed(() => store.state.carriers.rulesSyntax || []);
const rulesAssessmentStatus = computed(
  () =>
    store.state.carriers.failedRulesAssessmentData?.rules_assessment
      ?.overall_result
);
const failedAssessment = computed(
  () => store.state.carriers.failedRulesAssessmentData
);

const classifications = computed(() => {
  const data =
    store.state.carriers.failedRulesAssessmentData?.rules_assessment
      ?.classifications;
  return transformClassifications(data);
});

function transformClassifications(data) {
  if (!data) return [];
  return Object.entries(data).map(([name, classification]) => ({
    id: classification.classification_id,
    name,
    result: classification.result,
    rules: Object.fromEntries(
      Object.entries(classification)
        .filter(
          ([key]) =>
            key !== 'classification_id' &&
            key !== 'result' &&
            key !== 'sets_requiring_override_file'
        )
        .map(([key, value]) => [
          key,
          {
            result: value,
            override: null,
            is_override_file_required: false,
          },
        ])
    ),
  }));
}

function calculateRules(result) {
  const seenRules = new Set();

  classifications.value.forEach((el) => {
    Object.keys(el.rules)
      .filter((key) => el.rules[key]?.result === result && key !== 'result')
      .forEach((rule) => {
        if (!seenRules.has(rule)) {
          seenRules.add(rule);
        }
      });
  });

  return Array.from(seenRules);
}

const uniqueFailedRules = computed(() => calculateRules('fail'));
const uniqueIncompleteRules = computed(() => calculateRules('incomplete'));

const failedRulesByClassification = computed(() =>
  classifications.value
    .map((el) => {
      const rules = Object.entries(el.rules)
        .filter(([_, rule]) => rule.result === 'fail')
        .map(([key, rule]) => ({ rule: key, value: rule.result }));

      return rules.length > 0
        ? {
            name: el.name,
            id: el.id,
            rules,
          }
        : null;
    })
    .filter(Boolean)
);

const incompleteRulesByClassification = computed(() =>
  classifications.value
    .map((el) => {
      const rules = Object.entries(el.rules)
        .filter(([_, rule]) => rule.result === 'incomplete')
        .map(([key, rule]) => ({ rule: key, value: rule.result }));

      return rules.length > 0
        ? {
            name: el.name,
            id: el.id,
            rules,
          }
        : null;
    })
    .filter(Boolean)
);

const totalRules = computed(() => calculateRules('').length);

onMounted(async () => {
  if (!failedAssessment.value) {
    const auth = await store.dispatch('authorizations/createAuthorization');
    store.commit('carriers/setFailedRulesAssessmentData', auth?.data);
  }
  localStorage.removeItem('redirect_url_after_sigin');
});

watchEffect(() => {
  console.log('Loading state:', loading.value);
  console.log('Rules Assessment Status:', rulesAssessmentStatus.value);
  console.log(
    'Failed Rules by Classification:',
    failedRulesByClassification.value
  );
  console.log(
    'Incomplete Rules by Classification:',
    incompleteRulesByClassification.value
  );
});
</script>
